import React, { useMemo, useState, useEffect } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../router/all_routes";
import { useContentList } from "../hooks/useContent";
import { useQuery } from "@tanstack/react-query";
import { getCategories } from "../../../api/courses";
import { CourseLevel } from "../../../core/common/data/json/courses";
import { Pagination } from "antd";
import { calculateDiscount, rupiahFormatter } from "../../../utils/helpers";

const CourseGrid = ({ type }: { type?: string }) => {
  const navigate = useNavigate();

  const {
    content,
    isLoading,
    isRefetching,
    isError,
    error,
    currentPageData,
    total,
    selectedCategories,
    selectedLevels,
    limit,
    handleSearchInputChange,
    onPaginationChange,
    clearAllFilters,
    getCategorySlug,
  } = useContentList({ type, enableUrlParams: true });

  interface CourseGridProps {
    type?: "Course" | "Live_Teaching" | "CBT";
  }

  const apiType =
    type === "Course"
      ? "course"
      : type === "Live_Teaching"
        ? "live_teaching"
        : type === "CBT"
          ? "cbt"
          : undefined;

  const { data } = useQuery({
    queryKey: ["categories", { page: 1, limit: 100, type: apiType }],
    queryFn: () => getCategories({ page: 1, limit: 100, type: apiType }, false),
    refetchOnWindowFocus: false,
  });

  const dataCategories = data?.data;

  // Countdown state untuk setiap item
  const [countdowns, setCountdowns] = useState<{
    [key: string]: {
      days: number;
      hours: number;
      minutes: number;
      seconds: number;
    };
  }>({});

  // Countdown effect untuk semua item
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const newCountdowns: {
        [key: string]: {
          days: number;
          hours: number;
          minutes: number;
          seconds: number;
        };
      } = {};

      // Hitung countdown untuk setiap item Live_Teaching
      content.forEach((item: any) => {
        if (item.start_date && type === "Live_Teaching") {
          const startDate = new Date(item.start_date).getTime();
          const timeLeft = startDate - now;

          if (timeLeft > 0) {
            const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
            const hours = Math.floor(
              (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
            );
            const minutes = Math.floor(
              (timeLeft % (1000 * 60 * 60)) / (1000 * 60),
            );
            const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

            newCountdowns[item.id] = { days, hours, minutes, seconds };
          } else {
            newCountdowns[item.id] = {
              days: 0,
              hours: 0,
              minutes: 0,
              seconds: 0,
            };
          }
        }
      });

      setCountdowns(newCountdowns);
    }, 1000);

    return () => clearInterval(timer);
  }, [content, type]);

  const startResult = useMemo(
    () => (total > 0 ? (currentPageData - 1) * limit + 1 : 0),
    [total, currentPageData, limit],
  );
  const endResult = useMemo(
    () => (total > 0 ? Math.min(currentPageData * limit, total) : 0),
    [total, currentPageData, limit],
  );

  // Handler pagination dibagi supaya klik nomor halaman tidak ikut mereset page
  const handlePageChange = (page: number, pageSize: number) => {
    if (pageSize !== limit) return;
    onPaginationChange(page, pageSize);
  };

  const handlePageSizeChange = (_current: number, size: number) => {
    onPaginationChange(1, size);
  };

  // --- Fungsi untuk membuat link dengan filter ---
  const createFilterLink = (categoryId?: string, level?: string) => {
    if (!type) return "#";

    const baseRoutes = {
      Course: all_routes.jadiCourse,
      Live_Teaching: all_routes.liveTeaching,
      CBT: all_routes.jadiUkom,
    };

    const categoryRoutes = {
      Course: all_routes.jadiCourseWithCategory,
      Live_Teaching: all_routes.liveTeachingWithCategory,
      CBT: all_routes.jadiUkomWithCategory,
    };

    if (categoryId) {
      const categorySlug = getCategorySlug(categoryId);
      if (categorySlug) {
        return (
          categoryRoutes[type as keyof typeof categoryRoutes]?.replace(
            ":category",
            categorySlug,
          ) || "#"
        );
      }
    }

    return baseRoutes[type as keyof typeof baseRoutes] || "#";
  };

  // --- Handler untuk navigasi dengan filter ---
  const handleCategoryFilter = (categoryId: string, checked: boolean) => {
    if (checked) {
      const link = createFilterLink(categoryId);
      navigate(link);
    } else {
      // Jika uncheck, kembali ke halaman utama
      const baseRoute =
        type === "Course"
          ? all_routes.jadiCourse
          : type === "Live_Teaching"
            ? all_routes.liveTeaching
            : all_routes.jadiUkom;
      navigate(baseRoute);
    }
  };

  // --- Custom handlers untuk checkbox ---
  const handleCategoryCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value, checked } = e.target;
    handleCategoryFilter(value, checked);
  };

  // --- Fungsi untuk mendapatkan breadcrumb title ---
  const getBreadcrumbTitle = () => {
    if (selectedCategories.length > 0 && dataCategories) {
      const category = dataCategories.find(
        (cat: any) => cat.id?.toString() === selectedCategories[0],
      );
      if (category) {
        return `${
          type === "Course"
            ? "Courses"
            : type === "Live_Teaching"
              ? "Live Teaching"
              : "CBT"
        } - ${category.name}`;
      }
    }

    if (selectedLevels.length > 0) {
      const level = CourseLevel.find(
        (level) => level.value === selectedLevels[0],
      );
      if (level) {
        return `${level.label} Level ${
          type === "Course"
            ? "Courses"
            : type === "Live_Teaching"
              ? "Live Teaching"
              : "CBT"
        }`;
      }
    }

    return type === "Course"
      ? "E-Courses"
      : type === "Live_Teaching"
        ? "Live Teaching"
        : "CBT";
  };

  //fungsi untuk merender count category
  const renderCountCategory = (category: any) => {
    switch (type) {
      case "Course":
        return category?.course_count;
      case "Live_Teaching":
        return category?.live_teaching_count;
      case "CBT":
        return category?.cbt_count;
      default:
        return 0;
    }
  };

  // --- Fungsi untuk mendapatkan detail route sesuai type ---
  const getDetailRoute = (type?: string, id?: string) => {
    let route = "";
    switch (type) {
      case "Course":
        route = all_routes.jadiCourseDetail;
        break;
      case "Live_Teaching":
        route = all_routes.liveTeachingDetail;
        break;
      case "CBT":
        route = all_routes.jadiUkomDetail;
        break;
      default:
        route = all_routes.jadiCourseDetail;
    }
    return route.replace(":id", id || "");
  };

  return (
    <>
      <Breadcrumb title={getBreadcrumbTitle()} />

      {/* Course */}
      <section className="course-content">
        <div className="container">
          <div className="row align-items-baseline">
            <div className="col-lg-3">
              <div className="filter-clear">
                <div className="clear-filter mb-4 pb-lg-2 d-flex align-items-center justify-content-between">
                  <h5>
                    <i className="feather-filter me-2" />
                    Filters
                  </h5>
                  <Link
                    to="/e-course"
                    className="clear-text"
                    onClick={clearAllFilters}
                  >
                    Clear All
                  </Link>
                </div>
                <div className="accordion accordion-customicon1 accordions-items-seperate">
                  {/* Filter Categories */}
                  <div className="accordion-item">
                    <h2 className="accordion-header" id="headingcustomicon1One">
                      <Link
                        to="#"
                        className="accordion-button"
                        data-bs-toggle="collapse"
                        data-bs-target="#collapsecustomicon1One"
                        aria-expanded="true"
                        aria-controls="collapsecustomicon1One"
                      >
                        Categories <i className="fa-solid fa-chevron-down" />
                      </Link>
                    </h2>
                    <div
                      id="collapsecustomicon1One"
                      className="accordion-collapse collapse show"
                      aria-labelledby="headingcustomicon1One"
                      data-bs-parent="#accordioncustomicon1Example"
                    >
                      <div className="accordion-body">
                        {dataCategories?.map((category: any) => (
                          <div key={category.name}>
                            <label className="custom_check">
                              <input
                                type="checkbox"
                                name="category"
                                value={category.id}
                                checked={selectedCategories.includes(
                                  category.id?.toString(),
                                )}
                                onChange={handleCategoryCheckboxChange}
                              />
                              <span className="checkmark" /> {category.name} (
                              {renderCountCategory(category) || 0})
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-9">
              {/* Filter & Search */}
              <div className="showing-list mb-4">
                <div className="row align-items-center">
                  <div className="col-lg-4">
                    <div className="show-result text-center text-lg-start">
                      <h6 className="fw-medium">
                        Showing {startResult} - {endResult} of {total} results
                      </h6>
                    </div>
                  </div>
                  <div className="col-lg-8">
                    <div className="show-filter add-course-info">
                      <form action="#">
                        <div className="d-sm-flex justify-content-center justify-content-lg-end mb-1 mb-lg-0">
                          <div className=" search-group">
                            <i className="isax isax-search-normal-1" />
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search"
                              onChange={handleSearchInputChange}
                            />
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Filter */}

              {/* Course List */}
              {(isLoading || isRefetching) && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading content...</p>
                </div>
              )}

              {isError && (
                <div className="text-center py-5 text-danger">
                  <p>
                    Error loading content: {error?.message || "Unknown error"}
                  </p>
                  <p>Please try again later.</p>
                </div>
              )}

              {!isLoading &&
                !isError &&
                !isRefetching &&
                content.length === 0 && (
                  <div className="text-center py-5">
                    <p>No content found matching your criteria.</p>
                  </div>
                )}

              <div className="row">
                {!isLoading &&
                  !isError &&
                  !isRefetching &&
                  content.map(
                    (
                      item: any, // Gunakan 'item' karena ini konten umum
                    ) => (
                      <div className="col-xl-4 col-md-6 mb-3" key={item.id}>
                        <div
                          className={`course-item-two course-item mx-0${type === 'Live_Teaching' ? ' live-card' : ''}`}
                          style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <div className={`${type === 'Live_Teaching' ? '' : 'course-img'}`}>
                            <Link to={getDetailRoute(type, item.id)}>
                              <ImageWithBasePath
                                src={""}
                                srcApi={
                                  item?.image || item?.poster || "/assets/img/course/course-48.jpg"
                                }
                                fallbackSrc="/assets/img/course/course-48.jpg"
                                alt={item?.title || 'img'}
                                className="img-fluid"
                                isLandscape={false}
                                objectFit={type === 'Live_Teaching' ? 'contain' : undefined}
                                style={type === 'Live_Teaching' ? { borderRadius: '20px' } : undefined}
                              />
                            </Link>
                            <div className="position-absolute start-0 top-0 d-flex align-items-start w-100 z-index-2 p-3"></div>
                          </div>
                          <div
                            className="course-content"
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <div className="d-flex align-items-center">
                                {item.start_date &&
                                  item.end_date &&
                                  type === "Live_Teaching" && (
                                    <>
                                      {(() => {
                                        const now = new Date().getTime();
                                        const startDate = new Date(
                                          item.start_date,
                                        ).getTime();
                                        const endDate = new Date(
                                          item.end_date,
                                        ).getTime();

                                        if (now > endDate) {
                                          return (
                                            <span className="badge mb-0 bg-info">
                                              Event Selesai
                                            </span>
                                          );
                                        }

                                        if (
                                          now >= startDate &&
                                          now <= endDate
                                        ) {
                                          return (
                                            <span className="badge mb-0 bg-success">
                                              Live Now
                                            </span>
                                          );
                                        }

                                        if (now < startDate) {
                                          const itemCountdown = countdowns[
                                            item.id
                                          ] || {
                                            days: 0,
                                            hours: 0,
                                            minutes: 0,
                                            seconds: 0,
                                          };

                                          // Tampilkan format yang berbeda berdasarkan jumlah hari
                                          let countdownText = "";
                                          if (itemCountdown.days > 0) {
                                            countdownText = `${
                                              itemCountdown.days
                                            } hari ${itemCountdown.hours
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.minutes
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.seconds
                                              .toString()
                                              .padStart(2, "0")}`;
                                          } else {
                                            countdownText = `${itemCountdown.hours
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.minutes
                                              .toString()
                                              .padStart(
                                                2,
                                                "0",
                                              )} : ${itemCountdown.seconds
                                              .toString()
                                              .padStart(2, "0")}`;
                                          }

                                          return (
                                            <span className="badge mb-0 bg-warning">
                                              {countdownText}
                                            </span>
                                          );
                                        }
                                      })()}
                                    </>
                                  )}
                                {type === "Course" && (
                                  <>
                                    <span className="badge mb-0 bg-info">
                                      {`${item.student_count || 0} Siswa`}
                                    </span>
                                  </>
                                )}
                                {type === "CBT" && (
                                  <>
                                    <span className="badge mb-0 bg-info">
                                      {`${item.questions_count || 0} Soal`}
                                    </span>
                                  </>
                                )}
                              </div>
                              <span className="badge badge-light rounded-pill bg-light d-inline-flex align-items-center fs-13 fw-medium mb-0">
                                {
                                  dataCategories?.find(
                                    (value: any) =>
                                      value?.id?.toString() ===
                                      item?.category_id,
                                  )?.name
                                }
                              </span>
                            </div>
                            <h6
                              className="title mb-2"
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: "1.4",
                                minHeight: "2.8em", // 2 baris x 1.4 line-height
                                maxHeight: "2.8em",
                              }}
                            >
                              <Link to={getDetailRoute(type, item.id)}>
                                {item.title}
                              </Link>
                            </h6>
                            <div className="d-flex align-items-center">
                              <div className="price">
                                {!!item?.discount_type ? (
                                  <h5>
                                    {calculateDiscount(
                                      item?.price,
                                      item?.discount,
                                      item?.discount_type,
                                    )}{" "}
                                    <span>{rupiahFormatter(item?.price)}</span>
                                  </h5>
                                ) : (
                                  <h5>{rupiahFormatter(item?.price)} </h5>
                                )}
                              </div>
                            </div>
                            <Link
                              to={getDetailRoute(type, item.id)}
                              className="btn btn-dark btn-sm d-inline-flex align-items-center mt-2"
                              style={{
                                width: "fit-content",
                              }}
                            >
                              {type === 'Live_Teaching' ? 'Lihat Kelas' : 'Lihat Detail'}
                              {type !== 'Live_Teaching' && (
                                <i className="isax isax-arrow-right-3 ms-1" />
                              )}
                            </Link>
                          </div>
                        </div>
                      </div>
                    ),
                  )}
              </div>
              {/* /Course List */}

              {/* Pagination */}
              {!isLoading &&
                !isError &&
                !isRefetching &&
                content.length > 0 && (
                  <Pagination
                    align="end"
                    current={currentPageData}
                    total={total}
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                    pageSize={limit}
                    showSizeChanger
                    pageSizeOptions={["12", "24", "48", "96"]}
                    onChange={handlePageChange}
                    onShowSizeChange={handlePageSizeChange}
                  />
                )}
              {/* /pagination */}
            </div>
          </div>
        </div>
      </section>
      {/* /Course */}
    </>
  );
};

export default CourseGrid;
